import type { RawAxiosRequestConfig } from 'axios'
import axios from 'axios'

import { useGlobalStore } from 'src/stores/global'
import { getApiToken } from './auth'

/** Import job request interface */
export interface ImportJobRequest {
  inputBlobUri: string
  outputBlobUri: string
}

/** Import job response interface (from Azure Digital Twins API) */
export interface ImportJob {
  id: string
  inputBlobUri: string
  outputBlobUri: string
  createdDateTime: string
  lastActionDateTime: string
  finishedDateTime?: string
  purgeDateTime: string
  status:
    | 'notstarted'
    | 'running'
    | 'succeeded'
    | 'failed'
    | 'cancelled'
    | 'cancelling'
    | 'partiallySucceeded'
  error?: {
    code: string
    message: string
    details?: Array<{
      code: string
      message: string
    }>
  }
}

/** Import job status interface for polling */
export interface ImportJobWithStatus extends ImportJob {
  errorCount?: number
  modelsCreated?: number
  twinsCreated?: number
  relationshipsCreated?: number
}

class TwinsJobsApiService {
  private _config: RawAxiosRequestConfig<unknown> | undefined

  /**
   * Create a new import job
   * @param jobId The unique identifier for the import job
   * @param inputBlobUri The path to the input blob URI (can be blob ID or full URI)
   * @param outputBlobUri Optional output blob URI, will be generated if not provided
   * @returns The created import job
   */
  public async createImportJob(
    jobId: string,
    inputBlobUri: string,
    outputBlobUri?: string
  ): Promise<ImportJob> {
    await this.initialize()

    const { environment } = useGlobalStore()
    if (!environment) throw new Error('Environment not found')

    // Generate input blob URI if only blob ID is provided
    let finalInputBlobUri = inputBlobUri
    if (!inputBlobUri?.startsWith('https://')) {
      finalInputBlobUri = `https://${environment.storageHost}/${environment.storageContainer}/${inputBlobUri}`
    }

    // Generate output blob URI if not provided
    let finalOutputBlobUri = outputBlobUri
    if (!finalOutputBlobUri) {
      if (inputBlobUri.startsWith('https://')) {
        // Extract blob name from full URI and append suffix
        const blobName = inputBlobUri.split('/').pop() || 'output'
        const baseName = blobName.split('.')[0]
        finalOutputBlobUri = inputBlobUri.replace(blobName, `${baseName}-output.json`)
      } else {
        // Input is blob ID, generate output blob URI
        const baseName = inputBlobUri.split('.')[0]
        finalOutputBlobUri = `https://${environment.storageHost}/${environment.storageContainer}/${baseName}-output.json`
      }
    }

    const requestBody: ImportJobRequest = {
      inputBlobUri: finalInputBlobUri,
      outputBlobUri: finalOutputBlobUri
    }

    const response = await axios.put<ImportJob>(
      `${import.meta.env.twinsProxy}jobs/imports/${jobId}?api-version=2023-10-31`,
      requestBody,
      this._config
    )

    return response.data
  }

  /**
   * Get import job status by ID
   * @param jobId The import job ID
   * @returns The import job status
   */
  public async getImportJobStatus(jobId: string): Promise<ImportJob> {
    await this.initialize()

    const response = await axios.get<ImportJob>(
      `${import.meta.env.twinsProxy}jobs/imports/${jobId}?api-version=2023-10-31`,
      this._config
    )

    return response.data
  }

  /**
   * Poll for import job status with periodic updates
   * @param jobId The import job ID
   * @param update Callback for status updates
   * @param done Callback when job is complete
   * @returns Promise that resolves when job is done
   */
  public async pollImportJobStatus(
    jobId: string,
    update?: (status: ImportJobWithStatus) => void,
    done?: (job: ImportJobWithStatus) => void
  ): Promise<ImportJob> {
    return new Promise<ImportJob>((resolve, reject) => {
      const pollStatus = async () => {
        try {
          const job = await this.getImportJobStatus(jobId)

          if (typeof update === 'function') {
            update(job)
          }

          if (job.status === 'running' || job.status === 'notstarted') {
            // Continue polling
            setTimeout(pollStatus, 2000)
          } else if (job.status === 'failed' || job.status === 'cancelled') {
            // Job failed or was cancelled
            const error = new Error(job.error?.message || `Import job ${job.status}`)
            error.name = job.error?.code || 'ImportJobError'
            reject(error)
          } else {
            // Job completed successfully
            if (typeof done === 'function') {
              done(job)
            }
            resolve(job)
          }
        } catch (error) {
          reject(error)
        }
      }

      pollStatus()
    })
  }

  /**
   * Run an import job with polling and callbacks
   * @param jobId The import job ID
   * @param inputBlobUri The input blob URI or blob ID
   * @param outputBlobUri Optional output blob URI
   * @param update Callback for status updates
   * @param done Callback when job is complete
   * @returns Promise that resolves when job is done
   */
  public async runImportJob(
    jobId: string,
    inputBlobUri: string,
    outputBlobUri?: string,
    update?: (status: ImportJobWithStatus) => void,
    done?: (job: ImportJob) => void
  ): Promise<ImportJob> {
    // Create the import job
    await this.createImportJob(jobId, inputBlobUri, outputBlobUri)

    // Poll for status
    return this.pollImportJobStatus(jobId, update, done)
  }

  /**
   * List all import jobs
   * @returns Array of import jobs
   */
  public async listImportJobs(): Promise<ImportJob[]> {
    await this.initialize()

    const response = await axios.get<{ value: ImportJob[] }>(
      `${import.meta.env.twinsProxy}jobs/imports?api-version=2023-10-31`,
      this._config
    )

    return response.data.value
  }

  /**
   * Cancel an import job
   * @param jobId The import job ID
   * @returns The cancelled import job
   */
  public async cancelImportJob(jobId: string): Promise<ImportJob> {
    await this.initialize()
    const response = await axios.post<ImportJob>(
      `${import.meta.env.twinsProxy}jobs/imports/${jobId}/cancel?api-version=2023-10-31`,
      {},
      this._config
    )

    return response.data
  }

  /**
   * Delete an import job
   * @param jobId The import job ID
   */
  public async deleteImportJob(jobId: string): Promise<void> {
    await this.initialize()

    await axios.delete(
      `${import.meta.env.twinsProxy}jobs/imports/${jobId}?api-version=2023-10-31`,
      this._config
    )
  }

  private async initialize() {
    const authResponse = await getApiToken()
    if (!authResponse?.accessToken || !authResponse.expiresOn) return

    const { environmentId } = useGlobalStore()
    if (!environmentId) throw new Error('Environment id not found')

    this._config = {
      headers: {
        Authorization: 'Bearer ' + authResponse.accessToken,
        'x-adt-id': environmentId
      }
    }
  }
}

export const twinsJobsApiService = new TwinsJobsApiService()
